import DecliningBalanceFlyoutSvg from 'ext-ui/svg/illustration/DecliningBalanceFlyoutSvg'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import TestId from 'src/components/TestId/TestId'
import { UserOption } from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { virtualCardsAllCardsUrl } from 'src/navigation/urls'
import {
  selectNullSafeCurrencyFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'
import { css } from 'styled-components'

interface UpdateVirtualCardStandardContentProps {
  cardApproverId: string | undefined
  currencyError?: boolean
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  recipient: UserListModel
  cardholder: UserListModel
  userOptions: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  putVirtualCardAndOpenToastAndCloseFlyout: (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ) => void
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  setScreen: (val: UpdateVirtualCardScreen) => void
  negativeButton?: {
    loading: boolean
    text: React.ReactNode | string
    onClick: () => void
  }
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  showSoftAlert?: boolean
  typeofCard?: VirtualCardTypes
  isBillPay?: boolean
}

const UpdateVirtualCardStandardContent: React.FC<
  UpdateVirtualCardStandardContentProps
> = (props) => {
  const translate = useTranslate()

  const queryParams = useSelector(selectQueryParams)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { analytics } = useAnalyticsContext()
  const userId = useSelector(selectUserIdFromState)
  const cardTypeStandardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const cardTypeGuestImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  const isDecliningBalance =
    props.typeofCard === VirtualCardTypes.decliningBalance

  const isCurrentUserCardApprover = userId === props.cardApproverId
  const isCurrentUserFundingSourceOwner =
    userId === props.virtualCard.cardholderId
  const isCurrentUserCardRequester = userId === props.virtualCard.recipientId
  const isFinalCardApprovalOrRejection =
    isCurrentUserCardApprover &&
    props.virtualCard.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval

  const baseFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      ...(props.isBillPay
        ? {
            vendorName: props.virtualCard.billPayMeta?.vendorName,
            vendorEmail: props.virtualCard.billPayVendorEmail,
            invoiceDateMoment: props.virtualCard.invoiceDateTimezonedMoment,
            invoiceNumber: props.virtualCard.billPayMeta?.invoiceNumber,
          }
        : {
            userEmails: props.virtualCardPermissions.canUpdateVirtualCard
              ? [props.recipient.email]
              : props.virtualCard.awaitingApprovalStatus ===
                  ApprovalStatus.AwaitingApproverRuleApproval
                ? [props.cardholder.email, props.recipient.email]
                : [props.cardholder.email],
          }),
      creditCardId: props.virtualCard.creditCardId,
      balanceDollars: props.virtualCard.balance,
      limitDollars: isDecliningBalance ? props.virtualCard.limit : undefined,
      notes: props.virtualCard.notes,
      validToMoment: props.virtualCard.validToTimezonedMoment,
      currency: props.virtualCard.currency,
      lowBalanceAlertEnabled:
        props.virtualCard.lowLimitAlert?.alertEnabled ?? false,
      lowBalanceThresholdDollars:
        props.virtualCard.lowLimitAlert?.amountThresholdCents != null
          ? toDollars(props.virtualCard.lowLimitAlert.amountThresholdCents)
          : undefined,
    }),
    [
      props.virtualCard,
      props.recipient,
      props.virtualCardPermissions,
      props.isBillPay,
    ],
  )

  // const newRequestInitialCreditCardId = props.creditCardOptions.length === 1 && props.virtualCard.canUpdateVirtualCard
  //   ? props.creditCardOptions[0].value
  //   : queryParams['creditCardId']
  const newRequestInitialCreditCardId =
    typeof queryParams.creditCardId === 'string'
      ? queryParams.creditCardId
      : undefined // TODO
  const selectedCreditCard =
    newRequestInitialCreditCardId !== undefined
      ? creditCards[newRequestInitialCreditCardId]
      : undefined
  const initialFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      ...(props.isBillPay
        ? {
            vendorName: props.virtualCard.billPayMeta?.vendorName,
            vendorEmail: props.virtualCard.billPayVendorEmail,
            invoiceDateMoment: props.virtualCard.invoiceDateTimezonedMoment,
            invoiceNumber: props.virtualCard.billPayMeta?.invoiceNumber,
          }
        : {
            userEmails: props.virtualCardPermissions.canUpdateVirtualCard
              ? [props.recipient.email]
              : props.virtualCard.awaitingApprovalStatus ===
                  ApprovalStatus.AwaitingApproverRuleApproval
                ? [props.cardholder.email, props.recipient.email]
                : [props.cardholder.email],
          }),
      creditCardId: props.currencyError
        ? undefined
        : newRequestInitialCreditCardId || props.virtualCard.creditCardId,
      balanceDollars: props.currencyError
        ? 0
        : props.virtualCard.pending?.balance || props.virtualCard.balance,
      limitDollars: isDecliningBalance ? props.virtualCard.limit : undefined,
      notes: props.virtualCard.notes,
      validToMoment:
        props.virtualCard.pending?.validToTimezonedMoment(
          props.virtualCard.timezone,
        ) || props.virtualCard.validToTimezonedMoment,
      currency: props.isBillPay
        ? props.virtualCard.currency
        : props.currencyError
          ? defaultCurrency
          : selectedCreditCard?.currency || props.virtualCard.currency,
      receiptRulesExempt: props.isBillPay
        ? undefined
        : props.virtualCard.receiptRulesExempt,
      lowBalanceAlertEnabled:
        props.virtualCard.lowLimitAlert?.alertEnabled ?? false,
      lowBalanceThresholdDollars:
        props.virtualCard.lowLimitAlert?.amountThresholdCents != null
          ? toDollars(props.virtualCard.lowLimitAlert.amountThresholdCents)
          : undefined,
    }),
    [
      props.virtualCard,
      props.recipient,
      newRequestInitialCreditCardId,
      props.virtualCardPermissions,
      props.isBillPay,
    ],
  )

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    if (isFinalCardApprovalOrRejection && !isCurrentUserCardRequester) {
      // TODO: add approve action and redux
      props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
        pendingRevisionId:
          props.virtualCard?.pending?.id !== undefined
            ? props.virtualCard.pending.id
            : undefined,
      })
    } else if (props.isBillPay) {
      props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
        displayName: flatFields.displayName,
        creditCardId: flatFields.creditCardId,
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : undefined,
        expenseDetails: Object.entries(expenseDetails)
          .filter((keyVal) => !!keyVal[1])
          .map(([key, val]) => ({ categoryId: key, labelId: val })),
        notes: flatFields.notes,
        recurs: false,
        receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
        validTo:
          flatFields.validToMoment !== undefined
            ? flatFields.validToMoment.format('YYYY-MM-DD')
            : undefined,
        pendingRevisionId: props.virtualCard.pending?.id,
        billPayMetaUpdate: {
          invoiceNumber: flatFields.invoiceNumber,
          invoiceDate: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
        },
        singleExactPay: flatFields.singleExactPay,
        lowLimitAlert: buildLowLimitAlert(
          flatFields,
          lowBalanceAlertFeatureEnabled,
        ),
      })
    } else {
      props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
        creditCardId: flatFields.creditCardId,
        displayName: flatFields.displayName,
        expenseDetails: Object.entries(expenseDetails)
          .filter((keyVal) => !!keyVal[1])
          .map(([key, val]) => ({ categoryId: key, labelId: val })),
        notes: flatFields.notes,
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : undefined,
        recurs: false,
        receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
        validTo:
          flatFields.validToMoment !== undefined
            ? flatFields.validToMoment.format('YYYY-MM-DD')
            : undefined,
        currency: flatFields.currency,
        singleExactPay: flatFields.singleExactPay,
        balanceLocked: isDecliningBalance || undefined,
        pendingRevisionId: props.virtualCard.pending?.id,
        receiptRulesExempt: flatFields.receiptRulesExempt,
        lowLimitAlert: buildLowLimitAlert(
          flatFields,
          lowBalanceAlertFeatureEnabled,
        ),
      })
    }

    const eventName =
      props.virtualCardPermissions.canUpdateVirtualCard ||
      isCurrentUserCardApprover
        ? props.virtualCard.isPending
          ? 'manage vcn request'
          : !props.virtualCard.pending && !props.virtualCard.isPending
            ? 'update vcn'
            : undefined
        : props.virtualCard.isPending
          ? 'update vcn request'
          : props.virtualCard.isActive
            ? 'request vcn update'
            : undefined
    const properties = {
      type_of_card: props.isBillPay
        ? VirtualCardTypes.billPay
        : props.virtualCard.cardTypeLabel.props.source,
      card_name: flatFields.displayName,
      credit_limit: '$' + flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_attachments:
        props.receiptAttachments !== undefined &&
        props.receiptAttachments.length > 0,
      add_notes: flatFields.notes !== undefined,
      ...(eventName === 'manage vcn request'
        ? {
            card_Created: true,
            request_status: 'Approve',
          }
        : {}),
      ...(eventName === 'update vcn'
        ? {
            receipt_rules_exempt: flatFields.receiptRulesExempt,
            is_card_creator: props.virtualCard.cardholderId === userId,
            bulk: props.typeofCard === VirtualCardTypes.bulk,
          }
        : {}),
    }
    if (eventName !== undefined) {
      analytics?.track(eventName, properties)
    }
  }

  if (props.isBillPay) {
    return (
      <ActionRightFlyoutContent
        title={props.virtualCard.updateFlyoutTitle(
          props.virtualCardPermissions,
        )}
        specificElements={{
          imageSrc: cardTypeGuestImage,
        }}
        cardChangeNotAllowed={true}
        permissions={{
          canUpdateVirtualCard:
            props.virtualCardPermissions.canUpdateVirtualCard &&
            !props.virtualCard.isConsumed,
          isCardPending: props.virtualCard.isPending,
        }}
        screen={UpdateVirtualCardScreen.Pick}
      >
        <TestId testId={'updateVirtualCardForm'}>
          <VirtualCardForm
            topText={
              <>
                {selectedVirtualCardIdsFromRedux !== undefined &&
                  selectedVirtualCardIdsFromRedux?.length > 1 && (
                    <SoftAlertBox
                      css={css`
                        padding: 16px 20px;
                        margin-bottom: 20px;
                      `}
                    >
                      {translate(
                        'You can only edit virtual cards you have created or have access to as a budget user',
                      )}
                    </SoftAlertBox>
                  )}
                {!isCurrentUserCardApprover
                  ? props.virtualCard.updateFormTopText(
                      props.virtualCardPermissions,
                    )
                  : undefined}
              </>
            }
            virtualCard={props.virtualCard}
            initialFlatFields={initialFlatFields}
            customAddressEditable
            physicalCardEditable
            updateProps={{
              baseFlatFields: baseFlatFields,
              timezone: props.virtualCard.timezone,
            }}
            buttonText={props.virtualCard.updateFormButtonText(
              props.virtualCardPermissions,
              isCurrentUserCardApprover,
            )}
            id={props.virtualCard
              .updateFormButtonText(
                props.virtualCardPermissions,
                isCurrentUserCardApprover,
              )
              .props.source.split(' ')
              .join('')}
            onSuccessfulSubmit={onSuccessfulSubmit}
            userFieldLabel={
              props.virtualCardPermissions.canUpdateVirtualCard
                ? translate('Recipient')
                : translate('Request From')
            }
            loading={props.puttingVirtualCard}
            recipientNamesEditable={false}
            recurrenceEditable={false}
            notesEditable
            amountProps={{
              amountEditable: true,
            }}
            creditCardEditable={
              props.virtualCardPermissions.canUpdateVirtualCard &&
              props.virtualCard.isPending &&
              props.virtualCard.awaitingApprovalStatus !==
                ApprovalStatus.AwaitingApproverRuleApproval
            }
            creditCardProps={
              !props.virtualCardPermissions.canUpdateVirtualCard ||
              (isCurrentUserCardApprover && !isCurrentUserFundingSourceOwner)
                ? undefined
                : {
                    isFormDisabled: props.isFormDisabled,
                    creditCardOptions: props.creditCardOptions,
                    labelCreditCardsAsAccounts:
                      props.labelCreditCardsAsAccounts,
                  }
            }
            attachmentsProps={{
              sectionHeader: translate('Invoice'),
              sectionSubheader: translate(
                'Use this space to attach a vendor invoice. You will be able to attach the settled invoice once the payment clears.',
              ),
              receiptAttachments: props.receiptAttachments,
              uploadingAttachmentThumbnails:
                props.uploadingAttachmentThumbnails,
              uploadAttachments: props.uploadAttachments,
              deleteAttachment: props.deleteAttachment,
            }}
            negativeButton={props.negativeButton}
            topLevelError={props.topLevelError}
            errorDetailMap={props.errorDetailMap}
            currencyError={props.currencyError}
            creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
            canUpdateVirtualCard={
              props.virtualCardPermissions.canUpdateVirtualCard
            }
            typeofCard={VirtualCardTypes.billPay}
            showCardApprovalSoftAlert={props.showSoftAlert}
            awaitingFinalApproval={
              props.virtualCard.awaitingApprovalStatus ===
              ApprovalStatus.AwaitingApproverRuleApproval
            }
            awaitingFundingSourceOwnerApproval={
              props.virtualCard.awaitingApprovalStatus ===
              ApprovalStatus.AwaitingCreditCardUserApproval
            }
            cardApproverId={props.cardApproverId}
            isRequest={props.virtualCard.isPending}
          />
        </TestId>
      </ActionRightFlyoutContent>
    )
  }

  return (
    <ActionRightFlyoutContent
      title={props.virtualCard.updateFlyoutTitle(props.virtualCardPermissions)}
      specificElements={{
        subtitle: isDecliningBalance
          ? translate('Declining Balance Card')
          : translate('Standard'),
        imageSrc: isDecliningBalance ? undefined : cardTypeStandardImage,
        imageComponent: isDecliningBalance ? (
          <DecliningBalanceFlyoutSvg />
        ) : undefined,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: translate('Change Card Type'),
          onChangeClick: (): void => {
            props.setScreen(UpdateVirtualCardScreen.Pick)
          },
        },
      }}
      cardChangeNotAllowed={
        !props.virtualCard.isPending &&
        (props.virtualCard.features.singleExactPay ||
          props.virtualCard.features.balanceLocked ||
          props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval)
      }
      permissions={{
        canUpdateVirtualCard:
          props.virtualCardPermissions.canUpdateVirtualCard &&
          !props.virtualCard.isConsumed,
        isCardPending: props.virtualCard.isPending,
      }}
      screen={UpdateVirtualCardScreen.Pick}
    >
      <TestId testId={'updateVirtualCardForm'}>
        <VirtualCardForm
          topText={
            <>
              {matchPath(
                calculatedBackgroundLocation.pathname,
                virtualCardsAllCardsUrl(),
              ) &&
                selectedVirtualCardIdsFromRedux !== undefined &&
                selectedVirtualCardIdsFromRedux?.length > 1 && (
                  <SoftAlertBox
                    css={css`
                      padding: 16px 20px;
                      margin-bottom: 20px;
                    `}
                  >
                    {translate(
                      'You can only edit virtual cards you have created or have access to as a budget user',
                    )}
                  </SoftAlertBox>
                )}
              {!isCurrentUserCardApprover
                ? props.virtualCard.updateFormTopText(
                    props.virtualCardPermissions,
                  )
                : undefined}
            </>
          }
          virtualCard={props.virtualCard}
          initialFlatFields={initialFlatFields}
          customAddressEditable
          physicalCardEditable
          updateProps={{
            baseFlatFields: baseFlatFields,
            timezone: props.virtualCard.timezone,
          }}
          buttonText={props.virtualCard.updateFormButtonText(
            props.virtualCardPermissions,
            isCurrentUserCardApprover,
          )}
          id={props.virtualCard
            .updateFormButtonText(
              props.virtualCardPermissions,
              isCurrentUserCardApprover,
            )
            .props.source.split(' ')
            .join('')}
          onSuccessfulSubmit={onSuccessfulSubmit}
          userOptions={props.userOptions}
          userFieldLabel={
            props.virtualCardPermissions.canUpdateVirtualCard
              ? translate('Recipient')
              : translate('Request From')
          }
          loading={props.puttingVirtualCard}
          recipientNamesEditable={false}
          recurrenceEditable={false}
          notesEditable
          amountProps={{
            amountEditable: true,
          }}
          creditCardEditable={
            props.virtualCardPermissions.canUpdateVirtualCard &&
            props.virtualCard.isPending &&
            props.virtualCard.awaitingApprovalStatus !==
              ApprovalStatus.AwaitingApproverRuleApproval
          }
          creditCardProps={
            !props.virtualCardPermissions.canUpdateVirtualCard ||
            (isCurrentUserCardApprover && !isCurrentUserFundingSourceOwner)
              ? undefined
              : {
                  isFormDisabled: props.isFormDisabled,
                  creditCardOptions: props.creditCardOptions,
                  labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
                }
          }
          attachmentsProps={{
            receiptAttachments: props.receiptAttachments,
            uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
            uploadAttachments: props.uploadAttachments,
            deleteAttachment: props.deleteAttachment,
          }}
          negativeButton={props.negativeButton}
          topLevelError={props.topLevelError}
          errorDetailMap={props.errorDetailMap}
          currencyError={props.currencyError}
          creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          canUpdateVirtualCard={
            props.virtualCardPermissions.canUpdateVirtualCard
          }
          typeofCard={
            isDecliningBalance
              ? VirtualCardTypes.decliningBalance
              : VirtualCardTypes.standard
          }
          lockAmountOnly={isDecliningBalance && !props.virtualCard.isPending}
          showCardApprovalSoftAlert={props.showSoftAlert}
          awaitingFinalApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          }
          awaitingFundingSourceOwnerApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingCreditCardUserApproval
          }
          cardApproverId={props.cardApproverId}
          isRequest={props.virtualCard.isPending}
        />
      </TestId>
    </ActionRightFlyoutContent>
  )
}

export default UpdateVirtualCardStandardContent
